import * as React from "react";
import { Activity, CheckCircle2, Inbox, TrendingUp, XCircle } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  StatCard,
} from "@eduviaos/ui-kit";
import type { AdmissionFunnelResponse } from "@/lib/admissionStages/api";

/**
 * Phase D1c — SA Admissions Analytics widget.
 *
 * High-signal KPI strip computed entirely from the funnel response that
 * the panel already loads (zero extra round-trips). Renders five tiles:
 *
 *   1. Total enquiries (lifetime — every row the tenant has on record)
 *   2. Active        (sum of all non-terminal stages — work-in-progress)
 *   3. Admitted      (sum of stages with terminal_outcome === "admitted")
 *   4. Rejected      (sum of stages with terminal_outcome === "rejected")
 *   5. Conversion %  (admitted / total, 1-decimal precision)
 *
 * Why this lives on the SA tenant-detail page:
 *   - Platform team needs to see at a glance whether a tenant is
 *     actually using admissions or just sitting at zero.
 *   - Conversion % surfaces tenants who are receiving leads but losing
 *     them — those are the ones the success team should call.
 *   - Rejection volume (alongside conversion) helps spot tenants whose
 *     funnel config is too restrictive (high reject rate) vs. tenants
 *     genuinely doing high-quality screening.
 *
 * What it deliberately does NOT do:
 *   - No "recent rejections" list — that needs a new SA-scoped BE
 *     endpoint to surface enquiry detail (currently `/api/admissions/
 *     enquiries` is tenant-scoped and not callable from the SA portal
 *     auth context). Tracked as a Phase D1d deferral.
 *   - No time-window comparison ("this week vs last week") — funnel
 *     endpoint returns an instant snapshot only. Time-series belongs in
 *     Phase D6 (Admissions analytics dashboard).
 *
 * Loading + empty states:
 *   - During load: 5 muted skeleton tiles so the layout doesn't jump.
 *   - On zero enquiries: tiles still render with `0` values (honest
 *     about the empty funnel rather than hiding the widget — the SA
 *     learns "this tenant has zero enquiries", which is itself signal).
 */

interface AdmissionsAnalyticsCardProps {
  funnel: AdmissionFunnelResponse | undefined;
  loading: boolean;
}

interface ComputedAnalytics {
  total: number;
  active: number;
  admitted: number;
  rejected: number;
  conversionPct: number | null; // null when total === 0 (avoid /0)
}

function computeAnalytics(funnel: AdmissionFunnelResponse | undefined): ComputedAnalytics {
  if (!funnel) {
    return { total: 0, active: 0, admitted: 0, rejected: 0, conversionPct: null };
  }
  let admitted = 0;
  let rejected = 0;
  let active = 0;
  for (const stage of funnel.stages) {
    if (stage.is_terminal) {
      if (stage.terminal_outcome === "admitted") {
        admitted += stage.count;
      } else if (stage.terminal_outcome === "rejected") {
        rejected += stage.count;
      }
    } else {
      active += stage.count;
    }
  }
  const total = funnel.total;
  const conversionPct = total > 0 ? (admitted / total) * 100 : null;
  return { total, active, admitted, rejected, conversionPct };
}

function formatPct(pct: number | null): string {
  if (pct === null) return "—";
  // 1 decimal place; suppress trailing .0 for whole numbers so 50.0% reads as 50%.
  const rounded = Math.round(pct * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}%` : `${rounded.toFixed(1)}%`;
}

export function AdmissionsAnalyticsCard({ funnel, loading }: AdmissionsAnalyticsCardProps) {
  const analytics = React.useMemo(() => computeAnalytics(funnel), [funnel]);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="grid gap-0.5">
            <CardTitle>Funnel analytics</CardTitle>
            <CardDescription>
              Operational read-out of this tenant's admissions performance — derived from the live
              funnel snapshot below. Conversion % is admitted ÷ total lifetime enquiries.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                aria-hidden="true"
                className="bg-muted/40 h-[124px] animate-pulse rounded-xl"
              />
            ))}
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <StatCard
              label="Total enquiries"
              value={analytics.total.toLocaleString()}
              caption="Lifetime — includes admitted + rejected + active"
              icon={<Inbox aria-hidden="true" />}
              tone="blue"
            />
            <StatCard
              label="Active"
              value={analytics.active.toLocaleString()}
              caption="Currently in-progress (non-terminal stages)"
              icon={<Activity aria-hidden="true" />}
              tone="violet"
            />
            <StatCard
              label="Admitted"
              value={analytics.admitted.toLocaleString()}
              caption="Reached a terminal admitted stage"
              icon={<CheckCircle2 aria-hidden="true" />}
              tone="green"
            />
            <StatCard
              label="Rejected"
              value={analytics.rejected.toLocaleString()}
              caption="Reached a terminal rejected stage"
              icon={<XCircle aria-hidden="true" />}
              tone="rose"
            />
            <StatCard
              label="Conversion"
              value={formatPct(analytics.conversionPct)}
              caption={
                analytics.conversionPct === null
                  ? "No enquiries yet"
                  : `${analytics.admitted.toLocaleString()} of ${analytics.total.toLocaleString()} admitted`
              }
              icon={<TrendingUp aria-hidden="true" />}
              tone="amber"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
